import { FC, useState, useEffect, useMemo, useRef } from 'react';

import { IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { Dropdown } from 'common-ui/Dropdown/Dropdown';
import * as echarts from 'echarts';
import { MultiValue, SingleValue } from 'react-select';
import { ModalNotStyled } from 'ui-kit';

import { ChartSkeletonLoader } from './ChartSkeleton';
import { chartColors } from '../../../../configs/chart.config';
import { IconButton } from '../../../../ui-kit';
import { QUERY_KEYS } from '../shared';

type TreasuryYieldsData = {
  'Treasury Yields': {
    labels: string[];
    series: {
      [key: string]: number[];
    };
  };
};

type TreasuryYieldsProps = {
  dataUrl?: string;
  width?: number;
  height?: number;
  className?: string;
  modalView?: boolean;
  onExpandClose?: () => void;
};

type Option = {
  label: string;
  value: string;
};

function isMultiValue<T>(value: SingleValue<T> | MultiValue<T>): value is MultiValue<T> {
  return Array.isArray(value);
}

export const TreasuryYields: FC<TreasuryYieldsProps> = ({
  dataUrl,
  width = 620,
  height = 382,
  className = '',
  modalView = false,
  onExpandClose,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const { data, isLoading } = useReactQuery<TreasuryYieldsData, Error>({
    queryKey: [QUERY_KEYS.TREASURY_YIELDS, dataUrl],
    queryFn: async () => {
      if (!dataUrl) throw new Error('No URL provided');
      const response = await fetch(dataUrl);
      return response.json();
    },
    enabled: !!dataUrl,
  });

  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
  const [labels, setLabels] = useState<string[]>([]);
  const [seriesData, setSeriesData] = useState<number[]>([]);
  const [seriesNames, setSeriesNames] = useState<string[]>([]);

  useEffect(() => {
    if (data) {
      const labels = data['Treasury Yields'].labels;
      const seriesNames = Object.keys(data['Treasury Yields'].series);
      setLabels(labels);
      setSeriesNames(seriesNames);
      setSelectedSeries(seriesNames[5]); // Default to first series
    }
  }, [data]);

  useEffect(() => {
    if (data && selectedSeries) {
      const series = data['Treasury Yields'].series[selectedSeries];
      setSeriesData(series);
    }
  }, [data, selectedSeries]);

  const handleSeriesChange = (selectedOption: MultiValue<Option> | SingleValue<Option> | null) => {
    if (!selectedOption || isMultiValue<Option>(selectedOption)) return;
    setSelectedSeries(selectedOption.value);
  };

  const makeChartOption = useMemo(() => {
    if (!labels.length || !seriesData.length || !selectedSeries) return null;

    const maxDataValue = Math.ceil(Math.max(...seriesData));
    const minDataValue = Math.floor(Math.min(...seriesData) - 1);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(66,66,66,0.95)',
        borderWidth: 0,
        textStyle: {
          color: '#fff',
          fontWeight: 'bold',
        },
        axisPointer: {
          type: 'line',
          lineStyle: {
            type: 'solid',
            color: '#BBC5D7',
            width: 1,
          },
        },
        formatter: (params: any) => {
          if (!params || !params.length) return '';
          const item = params[0];
          const value = typeof item.data === 'number' ? item.data.toFixed(2) : item.data;
          return `
            <div style="border-radius: 0.5rem;">
              <p style="
                margin: 0;
                font-weight: bold;
                font-size: 12px;
                color: white;
              ">${item.axisValue}</p>
              <p style="
                font-weight: bold;
                font-size: 12px;
                color: ${chartColors.colorPalette[0]};
              ">${item.seriesName}: ${value}</p>
            </div>
          `;
        },
      },
      legend: {
        show: false, // no legend since we use dropdown
      },
      grid: {
        top: 10,
        left: '8%',
        right: '3%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        name: 'Date',
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#BBC5D7',
          fontSize: 12,
          padding: [20, 0, 0, 0],
        },
        axisLine: {
          onZero: false,
          lineStyle: { color: '#868E9F' },
        },
        axisLabel: {
          color: '#BBC5D7',
        },
      },
      yAxis: {
        type: 'value',
        name: 'Yield %',
        nameLocation: 'middle',
        interval: modalView ? 0.5 : 1,
        nameTextStyle: {
          color: '#BBC5D7',
          fontSize: 12,
          padding: [0, 0, 20, 0],
        },
        axisLine: {
          lineStyle: { color: '#868E9F' },
        },
        axisLabel: {
          noZero: false,
          color: '#BBC5D7',
        },
        splitLine: {
          lineStyle: {
            color: '#28303E',
          },
        },
        min: minDataValue,
        max: maxDataValue,
      },
      series: [
        {
          name: selectedSeries,
          type: 'line',
          data: seriesData,
          showSymbol: false,
          smooth: false,
          lineStyle: {
            width: 2,
            color: chartColors.colorPalette[0],
            shadowBlur: 10,
            shadowColor: 'rgba(132, 144, 236, 0.53)',
          },
          itemStyle: {
            color: chartColors.colorPalette[0],
          },
          symbolSize: 7,
          symbol: 'circle',
          emphasis: {
            itemStyle: {
              color: chartColors.colorPalette[0],
              borderColor: '#fff',
              borderWidth: 2,
            },
          },
        },
      ],
    };
  }, [labels, seriesData, selectedSeries]);

  useEffect(() => {
    if (chartRef.current && makeChartOption) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }
      chartInstance.current.setOption(makeChartOption);
    }
  }, [makeChartOption]);

  const categoryOptions = seriesNames.map((seriesName) => ({
    value: seriesName,
    label: seriesName,
  }));

  if (isLoading || !data) {
    return <ChartSkeletonLoader width={width} height={height} />;
  }

  return (
    <div className={`relative mx-auto mt-16 w-fit rounded-lg bg-slate-950 px-4 py-3 ${className}`}>
      {!modalView ? (
        <ModalNotStyled
          trigger={
            <IconButton
              variant="icon-only"
              icon={<IconArrowsMaximize size={16} color="#e85ee5" />}
              className="absolute right-2 rounded-full p-2"
              onClick={() => {}}
            />
          }
        >
          {({ closeModal }) => (
            <TreasuryYields
              dataUrl={dataUrl}
              modalView={true}
              onExpandClose={closeModal}
              width={window.innerWidth * 0.8}
              height={window.innerHeight * 0.8}
            />
          )}
        </ModalNotStyled>
      ) : (
        <IconButton
          variant="icon-only"
          icon={<IconArrowsMinimize size={16} color="#e85ee5" />}
          className="absolute right-3 rounded-full p-3"
          onClick={onExpandClose}
        />
      )}

      <div className="flex justify-center">
        <Dropdown
          className="mb-4 ml-2 w-[120px]"
          options={categoryOptions}
          onChange={handleSeriesChange}
          placeholder="Select Series"
          value={categoryOptions.find((option) => option.value === selectedSeries)}
        />
      </div>
      <div className="relative">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(60,206,206,0.25) 0%, rgba(255,0,210,0) 70%)',
            opacity: 0.4,
          }}
        />
        <div
          ref={chartRef}
          style={{
            width,
            height,
            position: 'relative',
          }}
        />
      </div>
    </div>
  );
};
